import { Stack, Typography, useTheme } from "@mui/material";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { SignatureTabFooter } from "./SignatureTabFooter";
import { SignatureConfigSection } from "./SignatureConfigSection";
import { SignaturePreviewSection } from "./SignaturePreviewSection";
import { InfoIcon } from "components";

const schema = yup.object({
  signature_logo: yup.string().nullable(),
  position: yup.string().required("Position is required"),
  width: yup.number().required("Width is required"),
  height: yup.number().required("Height is required"),
  signature_text: yup.string().required("Text is required"),
  is_size_synced: yup.boolean().default(false),
  signature_html: yup.string().nullable(),
});

export const SignatureEditorSection = forwardRef(
  (
    {
      options,
      defaultValues,
      onSubmit,
      isLoading,
      previewValues,
      isValueEdited,
      showFooter = false,
      isUpdatingInProgress,
      showInfo = false,
      open,
    }: {
      options: { label: string; value: string; id: string }[];
      defaultValues: any;
      onSubmit: (data: any) => void;
      isLoading: boolean;
      previewValues: any;
      isValueEdited?: (isEdited: boolean) => void;
      showFooter?: boolean;
      isUpdatingInProgress?: boolean;
      showInfo?: boolean;
      open?: boolean;
    },
    ref,
  ) => {
    const defaultFormValues: any = useMemo(
      () => ({
        signature_logo: defaultValues?.signature_logo || "",
        position: defaultValues?.position || "left",
        width: defaultValues?.width || 100,
        height: defaultValues?.height || 100,
        signature_text: defaultValues?.signature_text || "",
        is_size_synced: defaultValues?.is_size_synced || false,
        signature_html: defaultValues?.signature_html || "",
      }),
      [defaultValues],
    );

    const methods = useForm({
      defaultValues: defaultFormValues,
      resolver: yupResolver(schema),
    });
    const [sortedOptions, setSortedOptions] = useState(options);
    useEffect(() => {
      const sortedOptions = options.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setSortedOptions(sortedOptions);
    }, [options]);

    const {
      handleSubmit,
      reset,
      watch,
      formState: { isDirty, dirtyFields },
    } = methods;

    const currentValues: any = watch();

    const hasActualChanges = useMemo(() => {
      if (!isDirty) return false;

      return Object.keys(dirtyFields).some((key) => {
        return (
          JSON.stringify(currentValues[key]) !==
          JSON.stringify(defaultFormValues[key])
        );
      });
    }, [currentValues, defaultFormValues, isDirty, dirtyFields]);

    useEffect(() => {
      reset(defaultFormValues);
    }, [defaultFormValues, reset, open]);

    useEffect(() => {
      isValueEdited?.(hasActualChanges);
    }, [hasActualChanges, isValueEdited]);

    const onSubmitData = handleSubmit((data) => {
      onSubmit(data);
    });

    useImperativeHandle(ref, () => ({
      submit: onSubmitData,
    }));

    const theme = useTheme();
    return (
      <FormProvider {...methods}>
        <Stack direction="row" spacing={2} padding={"16px"} height={"100%"}>
          <Stack
            flex={1}
            direction="column"
            spacing={2}
            sx={{
              borderRadius: "8px",
              bgcolor: theme.palette.darkBg.main,
              height: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <SignatureConfigSection
              options={options}
              isLoading={isLoading}
              showInfo={showInfo}
            />
            {showFooter && hasActualChanges && (
              <SignatureTabFooter
                isLoading={Boolean(isUpdatingInProgress)}
                onCancel={() => reset(defaultFormValues)}
                onSave={onSubmitData}
              />
            )}
          </Stack>

          <SignaturePreviewSection
            options={options}
            previewValues={previewValues}
          />
        </Stack>
      </FormProvider>
    );
  },
);
